import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";

const PREFIX = "journey:draft:";
const DEBOUNCE_MS = 250;
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SESSION_TOAST_FLAG = "journey:draft:restored-toast-shown";
const CHANGE_EVENT = "journey:draft-changed";

function emitDraftChange(fullKey) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { key: fullKey } }));
  } catch {}
}

function isEmptyValue(v) {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") {
    const vals = Object.values(v);
    if (vals.length === 0) return true;
    return vals.every((x) => isEmptyValue(x));
  }
  return false;
}

function readDraft(fullKey, initialValue) {
  if (!fullKey || typeof window === "undefined") return { value: initialValue, restored: false };
  try {
    const raw = localStorage.getItem(fullKey);
    if (!raw) return { value: initialValue, restored: false };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !("v" in parsed)) {
      return { value: initialValue, restored: false };
    }
    if (parsed.t && Date.now() - parsed.t > TTL_MS) {
      localStorage.removeItem(fullKey);
      return { value: initialValue, restored: false };
    }
    if (isEmptyValue(parsed.v)) {
      return { value: initialValue, restored: false };
    }
    return { value: parsed.v, restored: true };
  } catch {
    return { value: initialValue, restored: false };
  }
}

function writeDraft(fullKey, value) {
  if (!fullKey || typeof window === "undefined") return;
  try {
    if (isEmptyValue(value)) {
      const had = localStorage.getItem(fullKey) !== null;
      localStorage.removeItem(fullKey);
      if (had) emitDraftChange(fullKey);
    } else {
      localStorage.setItem(fullKey, JSON.stringify({ v: value, t: Date.now() }));
      emitDraftChange(fullKey);
    }
  } catch {
    // localStorage unavailable / quota — fail silently, draft just won't persist
  }
}

function maybeShowRestoreToast() {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(SESSION_TOAST_FLAG) === "1") return;
    sessionStorage.setItem(SESSION_TOAST_FLAG, "1");
    // microtask defer so it doesn't fire during render
    queueMicrotask(() => {
      toast("Draft restored", { duration: 2400, id: "draft-restored" });
    });
  } catch {}
}

/**
 * useDraft — auto-save and restore form input via localStorage.
 *
 * @param {string|null} key — unique key (e.g., `chat:${convId}`). Falsy ⇒ disabled.
 * @param {*} initialValue — fallback when no draft exists.
 * @returns [value, setValue, clearDraft]
 *
 * - Debounced 250ms write
 * - Restores instantly on mount; reloads when key changes
 * - Empty values delete the storage entry
 * - Shows "Draft restored" toast once per session if any restore happens
 */
export function useDraft(key, initialValue) {
  const fullKey = key ? `${PREFIX}${key}` : null;
  const initialRef = useRef(initialValue); // freeze first initial
  const lastKeyRef = useRef(fullKey);

  const [value, setValue] = useState(() => {
    const { value: v, restored } = readDraft(fullKey, initialRef.current);
    if (restored) maybeShowRestoreToast();
    return v;
  });

  // Reload when key changes (e.g., switching conversations)
  useEffect(() => {
    if (fullKey === lastKeyRef.current) return;
    lastKeyRef.current = fullKey;
    const { value: v, restored } = readDraft(fullKey, initialRef.current);
    setValue(v);
    if (restored) maybeShowRestoreToast();
  }, [fullKey]);

  // Debounced persist
  const timerRef = useRef(null);
  useEffect(() => {
    if (!fullKey) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      writeDraft(fullKey, value);
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, fullKey]);

  // Persist immediately on tab hide / pagehide so we don't lose the in-flight debounce
  useEffect(() => {
    if (!fullKey) return;
    const flush = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      writeDraft(fullKey, value);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [value, fullKey]);

  const clearDraft = () => {
    if (!fullKey) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    try {
      const had = localStorage.getItem(fullKey) !== null;
      localStorage.removeItem(fullKey);
      if (had) emitDraftChange(fullKey);
    } catch {}
  };

  return [value, setValue, clearDraft];
}

/**
 * useHasDraft — reactive boolean for "is there a non-empty draft at this key (or matching pattern)?"
 *
 * @param {string|RegExp|function} match — exact key (e.g. "chat:abc"), RegExp, or predicate (fullKey)=>bool
 * @returns boolean
 *
 * Subscribes to in-tab `journey:draft-changed` and cross-tab `storage` events for instant updates.
 * Whitespace-only / empty drafts are auto-deleted by writeDraft so this returns false in those cases.
 */
export function useHasDraft(match) {
  const compute = () => checkHasDraft(match);
  const [has, setHas] = useState(compute);

  useEffect(() => {
    const handler = () => setHas(checkHasDraft(match));
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener("storage", handler);
    // Re-check on focus in case another tab cleared/wrote
    window.addEventListener("focus", handler);
    // initial sync (if `match` changed between renders)
    setHas(checkHasDraft(match));
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener("storage", handler);
      window.removeEventListener("focus", handler);
    };
    // We intentionally do not depend on `match` — for stable string keys this is fine,
    // for RegExp/function callers a new prop will trigger re-render and the initial useState
    // re-computes via closure on next render. Stable values are the common case here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return has;
}

function checkHasDraft(match) {
  if (typeof window === "undefined") return false;
  try {
    if (typeof match === "string") {
      return localStorage.getItem(`${PREFIX}${match}`) !== null;
    }
    const isRegex = match instanceof RegExp;
    const isFn = typeof match === "function";
    if (!isRegex && !isFn) return false;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PREFIX)) continue;
      const tail = k.slice(PREFIX.length);
      if (isRegex && match.test(tail)) return true;
      if (isFn && match(tail)) return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Sweep stale drafts on app boot. Removes:
 *  - entries older than maxAgeMs (default 7d)
 *  - empty / malformed entries
 *  - entries without timestamps (legacy)
 */
export function pruneDrafts(maxAgeMs = TTL_MS) {
  if (typeof window === "undefined") return;
  try {
    const now = Date.now();
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PREFIX)) continue;
      try {
        const raw = localStorage.getItem(k);
        if (!raw) {
          toRemove.push(k);
          continue;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || !("v" in parsed)) {
          toRemove.push(k);
          continue;
        }
        if (!parsed.t || now - parsed.t > maxAgeMs) {
          toRemove.push(k);
          continue;
        }
        if (isEmptyValue(parsed.v)) {
          toRemove.push(k);
        }
      } catch {
        toRemove.push(k);
      }
    }
    for (const k of toRemove) {
      try {
        localStorage.removeItem(k);
      } catch {}
    }
    if (toRemove.length > 0) emitDraftChange(null);
  } catch {}
}
